import React, { useState } from 'react';
import './Application.css';
import api from '../services/api';

const Application = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    consent: false
  });
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      setError('Please agree to be contacted before submitting.');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match!');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/register', {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      });

      if (response.data.message) {
        setShowSuccess(true);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: '',
          consent: false
        });
      }
    } catch (err) {
      console.error('Registration error:', err);
      console.error('Error response:', err.response?.data);
      
      const errorMessage = err.response?.data?.message;
      
      if (errorMessage === 'Email already registered') {
        setError(
          'This email is already registered. Please login or use a different email. If you haven\'t verified your account yet, please check your email for the verification link.'
        );
      } else {
        setError(errorMessage || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="application" id="apply">
      <div className="container">
        <div className="application-content">
          <div className="application-left">
            <h3 className="section-header">TAKE ACTION</h3>
            <h2 className="section-title">
              CHECK YOUR <span className="title-highlight">ELIGIBILITY</span>
            </h2>
            <p className="section-description">
              Don't miss this opportunity. Only the first 50 students will receive assured job offers. Fill out the form and our team will contact you within 24 hours.
            </p>
            
            <div className="application-process">
              <h3>
                <svg className="icon-circle" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                WHAT HAPPENS NEXT?
              </h3>
              <div className="process-steps">
                <div className="process-step">
                  <div className="step-number">1</div>
                  <p>Our counselor will call you within 24 hours</p>
                </div>
                <div className="process-step">
                  <div className="step-number">2</div>
                  <p>Discuss your eligibility and answer your questions</p>
                </div>
                <div className="process-step">
                  <div className="step-number">3</div>
                  <p>Guide you through the application process</p>
                </div>
                <div className="process-step">
                  <div className="step-number">4</div>
                  <p>Help with visa and documentation</p>
                </div>
              </div>
            </div>

            <div className="application-image">
              <img 
                src="/images/aviation-students.jpg" 
                alt="Aviation Academy Students" 
                onError={(e) => e.target.src = 'https://via.placeholder.com/560x320/1e3a8a/ffffff?text=Aviation+Academy'}
              />
            </div>
          </div>

          <div className="application-right">
            <div className="application-form-wrapper">
              <div className="form-badge">50 Seats Only</div>
              <h3>CREATE YOUR ACCOUNT</h3>
              <p style={{fontSize: '14px', color: '#9ca3af', marginBottom: '20px'}}>
                Register now to start your aviation journey
              </p>
              
              {error && (
                <div style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}

              {showSuccess ? (
                <div style={{
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '12px',
                  padding: '30px',
                  textAlign: 'center'
                }}>
                  <div style={{fontSize: '48px', marginBottom: '15px'}}>✓</div>
                  <h4 style={{color: '#22c55e', marginBottom: '10px', fontSize: '20px'}}>Account Created!</h4>
                  <p style={{color: '#e5e7eb', marginBottom: '15px', fontSize: '14px'}}>
                    We've sent a verification email to <strong>{formData.email}</strong>
                  </p>
                  <p style={{color: '#9ca3af', fontSize: '13px', lineHeight: '1.6'}}>
                    Please check your email and click the verification link to activate your account. 
                    Then you can login and complete your application.
                  </p>
                  <button 
                    onClick={() => setShowSuccess(false)}
                    style={{
                      marginTop: '20px',
                      padding: '10px 24px',
                      background: '#f59e0b',
                      color: '#0a1628',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Register Another Account
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="application-form">
                  <div className="form-group">
                    <label htmlFor="fullName">Full Name *</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="+1 234 567 8900"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="password">Password *</label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      placeholder="Enter password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength="6"
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password *</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirm password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      minLength="6"
                      disabled={loading}
                    />
                  </div>
                  
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        id="consent"
                        name="consent"
                        checked={formData.consent}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      />
                      <span>I agree to be contacted by Aviator1903 and Worldwide Wings regarding this program. I understand that my information will be handled according to the privacy policy.</span>
                    </label>
                  </div>
                  
                  <button type="submit" className="btn btn-submit" disabled={loading}>
                    {loading ? 'Creating Account...' : 'Create Account'}
                    {!loading && (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3.33325 8H12.6666M12.6666 8L7.99992 3.33333M12.6666 8L7.99992 12.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </form>
              )}
              
              <p className="form-note">
                Already have an account? <a href="/login" style={{color: '#f59e0b', textDecoration: 'underline'}}>Login here</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Application;
